import { useState, useEffect, useCallback, useRef } from "react";
import {
  collectParagraphs,
  countDiffStatsFromString,
  findCorrespondingBlockInSnapshot,
  getVisibleParagraphSelector,
} from "@/lib/diffViewHtml";
import { VIEWER_CHROME_ATTRIBUTE } from "@/partials/document-renderer/utils/uniqueReferenceChunk";

/**
 * "diff"   — default inline diff view (green ins + red del markup)
 * "before" — older version: reconstructed HTML + pink highlights on removals only
 * "after"  — current version: reconstructed HTML + green highlights on additions only
 */
export type DiffFilter = "diff" | "before" | "after";

export interface DiffVersionNames {
  /** Short name of the older/compare-target version */
  before: string;
  /** Short name of the current/newer version */
  after: string;
}

/** Full processed HTML for each side of the active compare step — used to fix foldout when htmldiff omits del/ins. */
export interface DiffSnapshotHtml {
  before: string | null;
  after: string | null;
}

const CURRENT_CLASS = "diff-current";
const FOLDOUT_BTN_CLASS = "diff-expand-btn";
const FOLDOUT_CLASS = "diff-foldout";

/**
 * Produce a clean HTML string from a diff paragraph by removing one side of
 * the diff and unwrapping the other, yielding readable prose for each version.
 */
const buildCleanHTML = (
  paragraph: HTMLElement,
  mode: "before" | "after",
  docRoot: HTMLElement | null,
  snapshots: DiffSnapshotHtml
): string => {
  const clone = paragraph.cloneNode(true) as HTMLElement;
  const insBefore = clone.querySelectorAll("ins.diffins, ins.diffmod").length;
  const delBefore = clone.querySelectorAll("del.diffdel, del.diffmod").length;
  if (mode === "before") {
    clone.querySelectorAll("ins.diffins, ins.diffmod").forEach((el) => el.remove());
    clone.querySelectorAll("del.diffdel, del.diffmod").forEach((el) => {
      el.replaceWith(document.createTextNode(el.textContent ?? ""));
    });
  } else {
    clone.querySelectorAll("del.diffdel, del.diffmod").forEach((el) => el.remove());
    clone.querySelectorAll("ins.diffins, ins.diffmod").forEach((el) => {
      el.replaceWith(document.createTextNode(el.textContent ?? ""));
    });
  }
  let result = clone.innerHTML;

  // htmldiff-js sometimes emits insert-only (no <del>) or delete-only (no <ins>) in a block,
  // so stripping ins/del cannot reconstruct that side — pull the block from full snapshots.
  if (mode === "before" && delBefore === 0 && insBefore > 0 && snapshots.before && docRoot) {
    const snapBlock = findCorrespondingBlockInSnapshot(
      paragraph,
      snapshots.before,
      docRoot
    );
    if (snapBlock) {
      result = snapBlock.innerHTML;
    }
  } else if (
    mode === "after" &&
    insBefore === 0 &&
    delBefore > 0 &&
    snapshots.after &&
    docRoot
  ) {
    const snapBlock = findCorrespondingBlockInSnapshot(
      paragraph,
      snapshots.after,
      docRoot
    );
    if (snapBlock) {
      result = snapBlock.innerHTML;
    }
  }

  return result;
};

export interface UseDiffNavigationReturn {
  /** Total changed paragraphs across the whole diff — fixed, never changes with filter */
  allChangesCount: number;
  /** Total visible changed paragraphs respecting the active filter — used for navigation counter */
  totalChanges: number;
  /** Count of paragraphs containing insertions */
  insertions: number;
  /** Count of paragraphs containing deletions */
  deletions: number;
  /** 0-based index of the currently highlighted paragraph */
  currentIndex: number;
  goToNext: () => void;
  goToPrevious: () => void;
  filter: DiffFilter;
  setFilter: (filter: DiffFilter) => void;
}

/**
 * Manages navigation and filtering of inline diff changes produced by
 * the legislation comparison feature (M-23).
 *
 * Navigation moves between *paragraphs that contain changes*, not individual
 * ins/del elements — so a paragraph with multiple insertions counts as one stop.
 *
 * "Before" / "after" views use reconstructed HTML (see LegislationDocumentContent)
 * so the document layout matches the real version, with highlight spans only.
 *
 * When in "diff" view, the currently navigated paragraph gets an expand button
 * injected after it. Clicking it reveals a fold-out panel showing the clean
 * before and after versions of the paragraph side-by-side.
 */
export const useDiffNavigation = (
  docRootRef: React.RefObject<HTMLElement | null>,
  diffHtml: string | null,
  versionNames?: DiffVersionNames,
  snapshotHtml?: DiffSnapshotHtml | null
): UseDiffNavigationReturn => {
  const [insertions, setInsertions] = useState(0);
  const [deletions, setDeletions] = useState(0);
  const [allChangesCount, setAllChangesCount] = useState(0);
  const [totalChanges, setTotalChanges] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Default to "after" (current-version reconstructed view) so the first paint
  // shows readable text with soft highlights rather than the noisy inline diff.
  const [filter, setFilterState] = useState<DiffFilter>("after");

  const currentIndexRef = useRef(0);
  const visibleParagraphsRef = useRef<HTMLElement[]>([]);
  const filterRef = useRef<DiffFilter>("after");
  const versionNamesRef = useRef<DiffVersionNames | undefined>(versionNames);

  useEffect(() => {
    versionNamesRef.current = versionNames;
  }, [versionNames]);

  const snapshotRef = useRef<DiffSnapshotHtml>({
    before: null,
    after: null,
  });
  useEffect(() => {
    snapshotRef.current = {
      before: snapshotHtml?.before ?? null,
      after: snapshotHtml?.after ?? null,
    };
  }, [snapshotHtml?.before, snapshotHtml?.after]);

  const expandBtnRef = useRef<HTMLElement | null>(null);
  const foldoutRef = useRef<HTMLElement | null>(null);

  const removeFoldoutDOM = useCallback(() => {
    expandBtnRef.current?.remove();
    expandBtnRef.current = null;
    foldoutRef.current?.remove();
    foldoutRef.current = null;
  }, []);

  const clearCurrentHighlight = useCallback(() => {
    docRootRef.current
      ?.querySelectorAll(`.${CURRENT_CLASS}`)
      .forEach((el) => el.classList.remove(CURRENT_CLASS));
  }, [docRootRef]);

  const injectExpandBtn = useCallback((paragraph: HTMLElement) => {
    if (filterRef.current !== "diff") return;

    // Our own chrome under `.doc-root`: tagged so the visible-text snapshot
    // skips it and search never hits its text (ADR 0001).
    const btn = document.createElement("button");
    btn.className = FOLDOUT_BTN_CLASS;
    btn.setAttribute(VIEWER_CHROME_ATTRIBUTE, "");
    btn.setAttribute("aria-expanded", "false");
    btn.setAttribute("type", "button");
    btn.innerHTML = `<span class="diff-expand-btn-label">Vis begge versioner</span><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>`;

    btn.addEventListener("click", () => {
      const isExpanded = btn.getAttribute("aria-expanded") === "true";

      if (isExpanded) {
        foldoutRef.current?.remove();
        foldoutRef.current = null;
        btn.setAttribute("aria-expanded", "false");
        btn.classList.remove(`${FOLDOUT_BTN_CLASS}--open`);
      } else {
        const names = versionNamesRef.current;

        const foldout = document.createElement("div");
        foldout.className = FOLDOUT_CLASS;
        foldout.setAttribute(VIEWER_CHROME_ATTRIBUTE, "");

        const beforePanel = document.createElement("div");
        beforePanel.className = "diff-foldout-before";
        if (names?.before) {
          const label = document.createElement("span");
          label.className = "diff-foldout-label";
          label.textContent = names.before;
          beforePanel.appendChild(label);
        }
        const beforeContent = document.createElement("div");
        beforeContent.className = "diff-foldout-content";
        beforeContent.innerHTML = buildCleanHTML(
          paragraph,
          "before",
          docRootRef.current,
          snapshotRef.current
        );
        beforePanel.appendChild(beforeContent);

        const afterPanel = document.createElement("div");
        afterPanel.className = "diff-foldout-after";
        if (names?.after) {
          const label = document.createElement("span");
          label.className = "diff-foldout-label";
          label.textContent = names.after;
          afterPanel.appendChild(label);
        }
        const afterContent = document.createElement("div");
        afterContent.className = "diff-foldout-content";
        afterContent.innerHTML = buildCleanHTML(
          paragraph,
          "after",
          docRootRef.current,
          snapshotRef.current
        );
        afterPanel.appendChild(afterContent);

        foldout.appendChild(beforePanel);
        foldout.appendChild(afterPanel);

        btn.insertAdjacentElement("afterend", foldout);
        foldoutRef.current = foldout;
        btn.setAttribute("aria-expanded", "true");
        btn.classList.add(`${FOLDOUT_BTN_CLASS}--open`);
      }
    });

    paragraph.insertAdjacentElement("afterend", btn);
    expandBtnRef.current = btn;
  }, [docRootRef]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const paragraphs = visibleParagraphsRef.current;
      if (paragraphs.length === 0) return;

      removeFoldoutDOM();
      clearCurrentHighlight();

      const target = paragraphs[index];
      if (!target) return;

      target.classList.add(CURRENT_CLASS);
      injectExpandBtn(target);
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    [clearCurrentHighlight, removeFoldoutDOM, injectExpandBtn]
  );

  // Signature of the last DOM scan (filter + count of diff markers in the DOM).
  // Lets the MutationObserver ignore our own class/button mutations and only
  // re-scan when the actual diff content changes.
  const lastScanSignatureRef = useRef<string>("");

  const runScan = useCallback(() => {
    const root = docRootRef.current;
    if (!root) return;

    filterRef.current = filter;

    const selector = getVisibleParagraphSelector(filter);
    const visibleParagraphs = collectParagraphs(root, selector);
    visibleParagraphsRef.current = visibleParagraphs;
    lastScanSignatureRef.current = `${filter}:${root.querySelectorAll(selector).length}`;

    setTotalChanges(visibleParagraphs.length);
    setCurrentIndex(0);
    currentIndexRef.current = 0;

    if (visibleParagraphs.length > 0) {
      scrollToIndex(0);
    }
  }, [filter, docRootRef, scrollToIndex]);

  // Compute stats from the diff string (source of truth) and do an initial scan.
  useEffect(() => {
    if (!diffHtml) {
      clearCurrentHighlight();
      removeFoldoutDOM();
      docRootRef.current?.classList.remove("diff-view-before", "diff-view-after");
      setInsertions(0);
      setDeletions(0);
      setAllChangesCount(0);
      setTotalChanges(0);
      setCurrentIndex(0);
      currentIndexRef.current = 0;
      visibleParagraphsRef.current = [];
      lastScanSignatureRef.current = "";
      filterRef.current = "diff";
      setFilterState("diff");
      return;
    }

    const stats = countDiffStatsFromString(diffHtml);
    setInsertions(stats.insertions);
    setDeletions(stats.deletions);
    setAllChangesCount(stats.allChangesCount);

    requestAnimationFrame(() => requestAnimationFrame(runScan));
  }, [diffHtml, filter, docRootRef, clearCurrentHighlight, removeFoldoutDOM, runScan]);

  // The compare HTML is rendered through `useDeferredValue` by the caller, so the
  // diff DOM can commit AFTER this hook's `diffHtml` effect already scanned an
  // empty/stale DOM. Observe the doc root and re-scan when the diff markers
  // actually change (guarded by a signature so our own class/button mutations
  // during navigation don't trigger a rescan loop).
  useEffect(() => {
    const root = docRootRef.current;
    if (!root || !diffHtml) return;

    let frame = 0;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const currentRoot = docRootRef.current;
        if (!currentRoot) return;
        const selector = getVisibleParagraphSelector(filterRef.current);
        const signature = `${filterRef.current}:${currentRoot.querySelectorAll(selector).length}`;
        if (signature === lastScanSignatureRef.current) return;
        runScan();
      });
    });

    observer.observe(root, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [docRootRef, diffHtml, runScan]);

  useEffect(() => {
    return () => {
      removeFoldoutDOM();
    };
  }, [removeFoldoutDOM]);

  const setFilter = useCallback(
    (newFilter: DiffFilter) => {
      setFilterState(newFilter);
      filterRef.current = newFilter;
      removeFoldoutDOM();
      docRootRef.current?.classList.remove("diff-view-before", "diff-view-after");
    },
    [docRootRef, removeFoldoutDOM]
  );

  const goToNext = useCallback(() => {
    const total = visibleParagraphsRef.current.length;
    if (total === 0) return;
    const nextIndex = (currentIndexRef.current + 1) % total;
    currentIndexRef.current = nextIndex;
    setCurrentIndex(nextIndex);
    scrollToIndex(nextIndex);
  }, [scrollToIndex]);

  const goToPrevious = useCallback(() => {
    const total = visibleParagraphsRef.current.length;
    if (total === 0) return;
    const prevIndex = (currentIndexRef.current - 1 + total) % total;
    currentIndexRef.current = prevIndex;
    setCurrentIndex(prevIndex);
    scrollToIndex(prevIndex);
  }, [scrollToIndex]);

  return {
    allChangesCount,
    totalChanges,
    insertions,
    deletions,
    currentIndex,
    goToNext,
    goToPrevious,
    filter,
    setFilter,
  };
};
